import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, LayersControl, ScaleControl, ZoomControl } from 'react-leaflet';
import LayerRenderer from './LayerRenderer';
import DATA_TYPES, { loadDynamicDataTypes } from '../config/dataTypes';
import 'leaflet/dist/leaflet.css';
import './MapEngine.css';

/**
 * MapEngine Component
 * 
 * Unified map display for all data types with:
 * - Layer toggle controls for each data type
 * - Custom icons and styling per layer
 * - Legend showing active layers
 * - Support for points, polygons, and other GeoJSON features
 * - Clustering and heatmap options per data type
 * 
 * Props:
 * - dataLayers: Array of { dataTypeId, data: [] } objects
 * - center: Initial map center [lat, lng]
 * - zoom: Initial zoom level
 * - height: Map container height
 * - enabledLayers: Array of initially enabled layer IDs (optional)
 * - onFeatureClick: Callback when a feature is clicked (optional)
 */
const MapEngine = ({
  dataLayers = [],
  center = [48.3809, -89.2477], // Default: Thunder Bay, Ontario
  zoom = 10,
  height = '600px',
  enabledLayers = null,
  onFeatureClick = null
}) => {
  const [dataTypeConfigs, setDataTypeConfigs] = useState({});
  const [activeLayers, setActiveLayers] = useState(new Set());
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [mapStats, setMapStats] = useState({
    totalFeatures: 0,
    byDataType: {}
  });

  // Load dynamic data types on mount
  useEffect(() => {
    const loadConfigs = async () => {
      const dynamicTypes = await loadDynamicDataTypes();
      setDataTypeConfigs({ ...DATA_TYPES, ...dynamicTypes });
    };
    loadConfigs();
  }, []);

  // Initialize active layers after configs are loaded
  useEffect(() => {
    if (Object.keys(dataTypeConfigs).length === 0) return;
    if (activeLayers.size > 0) return; // Only initialize once
    
    // Initialize with all layers enabled or use provided enabledLayers
    if (enabledLayers) {
      setActiveLayers(new Set(enabledLayers));
      return;
    }
    
    const initialLayers = new Set();
    
    dataLayers.forEach(layer => {
      const config = dataTypeConfigs[layer.dataTypeId];
      
      // If this data type has an iconPalette with categories, add each category
      if (config?.iconPalette && Object.keys(config.iconPalette).length > 1) {
        Object.keys(config.iconPalette).forEach(categoryName => {
          if (categoryName !== 'default') {
            initialLayers.add(`${layer.dataTypeId}-${categoryName}`);
          }
        });
      } else {
        // Standard data type, just add the dataTypeId
        initialLayers.add(layer.dataTypeId);
      }
    });
    
    setActiveLayers(initialLayers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypeConfigs, enabledLayers]);

  // Calculate map statistics
  useEffect(() => {
    const stats = {
      totalFeatures: 0,
      byDataType: {}
    };

    dataLayers.forEach(layer => {
      const count = layer.data ? layer.data.length : 0;
      stats.byDataType[layer.dataTypeId] = count;
      stats.totalFeatures += count;
    });

    setMapStats(stats);
  }, [dataLayers]);

  // Toggle layer visibility
  const toggleLayer = (dataTypeId) => {
    setActiveLayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataTypeId)) {
        newSet.delete(dataTypeId);
      } else {
        newSet.add(dataTypeId);
      }
      return newSet;
    });
  };

  // Get data type config
  const getDataTypeConfig = (dataTypeId) => {
    return dataTypeConfigs[dataTypeId] || null;
  };

  return (
    <div className="map-engine-container" style={{ height }}>
      {/* Layer Controls Panel */}
      <div className="layer-controls-panel">
        <h4 className="layer-controls-title">Map Layers</h4>
        <div className="layer-toggles">
          {dataLayers.map(layer => {
            const config = getDataTypeConfig(layer.dataTypeId);
            if (!config) return null;

            const isActive = activeLayers.has(layer.dataTypeId);
            const count = mapStats.byDataType[layer.dataTypeId] || 0;

            // If this data type has an iconPalette, show individual category toggles
            if (config.iconPalette && Object.keys(config.iconPalette).length > 1) {
              return (
                <div key={layer.dataTypeId} className="layer-group">
                  <div className="layer-group-title">{config.name}</div>
                  {Object.entries(config.iconPalette)
                    .filter(([key]) => key !== 'default')
                    .map(([categoryName, iconData]) => {
                      // Count items in this category
                      const categoryCount = layer.data?.filter(item => 
                        item.structuredData?.category === categoryName
                      ).length || 0;
                      
                      const categoryKey = `${layer.dataTypeId}-${categoryName}`;
                      const isCategoryActive = activeLayers.has(categoryKey);
                      
                      // Extract icon string from object if needed
                      const icon = typeof iconData === 'object' ? (iconData.icon || '📍') : iconData;
                      
                      return (
                        <div
                          key={categoryKey}
                          className={`layer-toggle sub-layer ${isCategoryActive ? 'active' : ''}`}
                          onClick={() => toggleLayer(categoryKey)}
                        >
                          <div className="layer-toggle-header">
                            <div className="layer-info">
                              <span className="layer-icon">{icon}</span>
                              <span className="layer-name">{categoryName}</span>
                            </div>
                            <div className="layer-stats">
                              <span className="layer-count">{categoryCount}</span>
                              <div
                                className="layer-indicator"
                                style={{ backgroundColor: isCategoryActive ? config.color : '#ccc' }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            }

            // Standard single-icon data type
            return (
              <div
                key={layer.dataTypeId}
                className={`layer-toggle ${isActive ? 'active' : ''}`}
                onClick={() => toggleLayer(layer.dataTypeId)}
              >
                <div className="layer-toggle-header">
                  <div className="layer-info">
                    <span className="layer-icon">{config.icon}</span>
                    <span className="layer-name">{config.name}</span>
                  </div>
                  <div className="layer-stats">
                    <span className="layer-count">{count}</span>
                    <div
                      className="layer-indicator"
                      style={{ backgroundColor: isActive ? config.color : '#ccc' }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Map Statistics */}
        <div className="map-statistics">
          <div className="stat-item">
            <span className="stat-label">Total Features:</span>
            <span className="stat-value">{mapStats.totalFeatures}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Active Layers:</span>
            <span className="stat-value">{activeLayers.size}</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="map-display-container">
        <MapContainer
          center={center}
          zoom={zoom}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          {/* Base Map Tiles */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street Map">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topographic">
              <TileLayer
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Render Data Layers */}
          {dataLayers.map(layer => {
            const config = getDataTypeConfig(layer.dataTypeId);
            if (!config) return null;

            // Check if this layer has category-based sub-layers
            if (config.iconPalette && Object.keys(config.iconPalette).length > 1) {
              // Render each active category as a separate layer
              return Object.keys(config.iconPalette)
                .filter(categoryName => categoryName !== 'default')
                .map(categoryName => {
                  const categoryKey = `${layer.dataTypeId}-${categoryName}`;
                  if (!activeLayers.has(categoryKey)) return null;

                  // Filter data for this specific category
                  const categoryData = (layer.data || []).filter(item =>
                    item.structuredData?.category === categoryName
                  );

                  return (
                    <LayerRenderer
                      key={categoryKey}
                      dataTypeConfig={config}
                      data={categoryData}
                      onFeatureClick={onFeatureClick}
                    />
                  );
                });
            }

            // Standard single-layer rendering
            if (!activeLayers.has(layer.dataTypeId)) return null;

            return (
              <LayerRenderer
                key={layer.dataTypeId}
                dataTypeConfig={config}
                data={layer.data || []}
                onFeatureClick={onFeatureClick}
              />
            );
          })}


          {/* Map Controls */}
          <ZoomControl position="bottomright" />
          <ScaleControl position="bottomleft" imperial={false} />
        </MapContainer>
      </div>

      {/* Legend - Disabled */}
      {/* {activeLayers.size > 0 && (
        <div className={`map-legend-floating ${showLegend ? 'expanded' : 'collapsed'}`}>
          <div className="legend-header">
            <h5 className="legend-title">Legend</h5>
            <button 
              className="legend-toggle"
              onClick={() => setShowLegend(!showLegend)}
              aria-label={showLegend ? 'Hide legend' : 'Show legend'}
            >
              {showLegend ? '▼ Hide' : '▶ Show'}
            </button>
          </div>
          {showLegend && (
            <div className="legend-items">
              {Array.from(activeLayers).map(dataTypeId => {
                const config = getDataTypeConfig(dataTypeId);
                if (!config) return null;

                return (
                  <div key={dataTypeId} className="legend-item">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: config.color }}
                    />
                    <span className="legend-icon">{config.icon}</span>
                    <span className="legend-label">{config.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )} */}
    </div>
  );
};

export default MapEngine;
